import { formatDateToFullDate } from "../../helpers/dates";
import { Locales } from "../enums/locales";
import { buildAutoRenewalBody, buildExpiringMembershipBody } from "./membership-renewal-body";
export const getDonationRenewalEmailVariables = (variables) => ({
    "Renewal date": formatDateToFullDate(variables.renewalDate, variables.locale),
    "Renewal amount": variables.renewalAmount || "",
    "Payment method": variables.paymentMethod || "",
});
const replaceDonationDefaultPlaceholders = (body, variables) => {
    const { firstName, renewalDate, locale, orgName, renewalAmount, paymentMethod } = variables;
    return body
        .replace(/\{\{First name\}\}/g, firstName === "N A" ? "" : firstName)
        .replace(/\{\{Renewal date\}\}/g, formatDateToFullDate(renewalDate, locale))
        .replace(/\[OrgName\]/g, orgName)
        .replace(/\{\{Renewal amount\}\}/g, renewalAmount || "")
        .replace(/\{\{Payment method\}\}/g, paymentMethod || "");
};
export const getDefaultDonationAutoRenewalSubject = (locale) => {
    if (locale === Locales.FR) {
        return "Votre don récurrent se renouvelle bientôt ✨";
    }
    if (locale === Locales.ES) {
        return "Tu donación recurrente se renueva pronto ✨";
    }
    if (locale === Locales.DE) {
        return "Ihre wiederkehrende Spende wird bald verlängert ✨";
    }
    return "Your recurring donation renews soon ✨";
};
const EMPTY_LINE_DONATION = "<p><br></p>";
export const getDefaultDonationAutoRenewalBody = (variables, donorDashboardUrl) => {
    const { locale } = variables;
    const defaultBody = locale === Locales.FR
        ? `<p>Bonjour {{First name}},</p>` +
            EMPTY_LINE_DONATION +
            `<p>Votre don récurrent soutenant [OrgName] <strong>se renouvellera le {{Renewal date}}</strong>.</p>` +
            EMPTY_LINE_DONATION +
            `<p><strong>Montant du renouvellement :</strong> {{Renewal amount}}<br/><strong>Méthode de paiement :</strong> {{Payment method}}</p>` +
            EMPTY_LINE_DONATION +
            `<p>Pour gérer votre don ou mettre à jour votre méthode de paiement, vous pouvez le faire <a href="${donorDashboardUrl}">ici</a>.</p>` +
            EMPTY_LINE_DONATION +
            `<p>Merci pour votre soutien continu !</p>` +
            EMPTY_LINE_DONATION +
            `<p>[OrgName]</p>`
        : locale === Locales.ES
            ? `<p>Hola {{First name}},</p>` +
                EMPTY_LINE_DONATION +
                `<p>Tu donación recurrente que apoya a [OrgName] <strong>se renovará el {{Renewal date}}</strong>.</p>` +
                EMPTY_LINE_DONATION +
                `<p><strong>Monto de renovación:</strong> {{Renewal amount}}<br/><strong>Método de pago:</strong> {{Payment method}}</p>` +
                EMPTY_LINE_DONATION +
                `<p>Para gestionar tu donación o actualizar tu método de pago, puedes hacerlo <a href="${donorDashboardUrl}">aquí</a>.</p>` +
                EMPTY_LINE_DONATION +
                `<p>¡Gracias por tu apoyo continuo!</p>` +
                EMPTY_LINE_DONATION +
                `<p>[OrgName]</p>`
            : locale === Locales.DE
                ? `<p>Hallo {{First name}},</p>` +
                    EMPTY_LINE_DONATION +
                    `<p>Ihre wiederkehrende Spende zugunsten von [OrgName] <strong>wird am {{Renewal date}} verlängert</strong>.</p>` +
                    EMPTY_LINE_DONATION +
                    `<p><strong>Verlängerungsbetrag:</strong> {{Renewal amount}}<br/><strong>Zahlungsmethode:</strong> {{Payment method}}</p>` +
                    EMPTY_LINE_DONATION +
                    `<p>Um Ihre Spende zu verwalten oder Ihre Zahlungsmethode zu aktualisieren, klicken Sie <a href="${donorDashboardUrl}">hier</a>.</p>` +
                    EMPTY_LINE_DONATION +
                    `<p>Vielen Dank für Ihre kontinuierliche Unterstützung!</p>` +
                    EMPTY_LINE_DONATION +
                    `<p>[OrgName]</p>`
                : `<p>Hi {{First name}},</p>` +
                    EMPTY_LINE_DONATION +
                    `<p>Your recurring donation supporting [OrgName] <strong>will renew on {{Renewal date}}</strong>.</p>` +
                    EMPTY_LINE_DONATION +
                    `<p><strong>Renewal amount:</strong> {{Renewal amount}}<br/><strong>Payment method:</strong> {{Payment method}}</p>` +
                    EMPTY_LINE_DONATION +
                    `<p>To manage your donation or update your payment method, you can do so <a href="${donorDashboardUrl}">here</a>.</p>` +
                    EMPTY_LINE_DONATION +
                    `<p>Thank you for your continued support!</p>` +
                    EMPTY_LINE_DONATION +
                    `<p>[OrgName]</p>`;
    return replaceDonationDefaultPlaceholders(defaultBody, variables);
};
const getMemberFullName = (variables) => [variables.memberFirstName, variables.memberLastName]
    .map((namePart) => namePart?.trim())
    .filter(Boolean)
    .join(" ");
export const getRenewalEmailVariables = (variables) => ({
    "Membership type": variables.membershipType,
    "Expiry date": formatDateToFullDate(variables.expiryDate, variables.locale),
    "Renewal amount": variables.renewalAmount || "",
    "Payment method": variables.paymentMethod || "",
    "Member first name": variables.memberFirstName?.trim() || "",
    "Member last name": variables.memberLastName?.trim() || "",
});
const MEMBER_VARIABLE_ONLY_GROUP = /\s*\((?:\s|&nbsp;|<span[^>]*data-custom-variable="\{\{Member (?:first|last) name\}\}"[^>]*>.*?<\/span>|\{\{Member (?:first|last) name\}\})+\)/g;
export const dropEmptyMemberParenthetical = (body, variables) => getMemberFullName(variables) ? body : body.replace(MEMBER_VARIABLE_ONLY_GROUP, "");
const replaceDefaultPlaceholders = (body, variables) => {
    const { firstName, membershipType, expiryDate, locale, orgName, renewalAmount, paymentMethod } = variables;
    return body
        .replace(/\{\{First name\}\}/g, firstName === "N A" ? "" : firstName)
        .replace(/\{\{Membership type\}\}/g, membershipType)
        .replace(/\{\{Expiry date\}\}/g, formatDateToFullDate(expiryDate, locale))
        .replace(/\[OrgName\]/g, orgName)
        .replace(/\{\{Renewal amount\}\}/g, renewalAmount || "")
        .replace(/\{\{Payment method\}\}/g, paymentMethod || "")
        .replace(/\{\{Member first name\}\}/g, variables.memberFirstName?.trim() || "")
        .replace(/\{\{Member last name\}\}/g, variables.memberLastName?.trim() || "");
};
const getMemberSuffix = (variables) => {
    const memberFullName = getMemberFullName(variables);
    return memberFullName ? ` (${memberFullName})` : "";
};
export const getDefaultExpiringMembershipSubject = (orgName, locale) => {
    if (locale === Locales.FR) {
        return `⏰ Il est temps de renouveler l'adhésion pour ${orgName}`;
    }
    if (locale === Locales.ES) {
        return `⏰ Es hora de renovar la membresía para ${orgName}`;
    }
    if (locale === Locales.DE) {
        return `⏰ Es ist Zeit, die Mitgliedschaft bei ${orgName} zu verlängern`;
    }
    return `⏰ It's time to renew the membership for ${orgName}`;
};
export const getDefaultAutoRenewalSubject = (locale) => {
    if (locale === Locales.FR) {
        return "L'adhésion se renouvelle dans 30 jours ✨";
    }
    if (locale === Locales.ES) {
        return "La membresía se renueva en 30 días ✨";
    }
    if (locale === Locales.DE) {
        return "Die Mitgliedschaft wird in 30 Tagen verlängert ✨";
    }
    return "Membership renewal in 30 days ✨";
};
export const FORM_URL_PLACEHOLDER = "{{formUrl}}";
export const DONOR_DASHBOARD_URL_PLACEHOLDER = "{{donorDashboardUrl}}";
const PLACEHOLDER_PARTS = {
    firstName: "{{First name}}",
    membershipType: "{{Membership type}}",
    expiryDate: "{{Expiry date}}",
    orgName: "[OrgName]",
    signature: "<p>[OrgName]</p>",
    renewalAmount: "{{Renewal amount}}",
    paymentMethod: "{{Payment method}}",
};
export const getDefaultExpiringMembershipBody = (variables, formUrl) => replaceDefaultPlaceholders(buildExpiringMembershipBody({ ...PLACEHOLDER_PARTS, memberSuffix: getMemberSuffix(variables), url: formUrl }, variables.locale), variables);
export const getDefaultAutoRenewalBody = (variables, donorDashboardUrl) => replaceDefaultPlaceholders(buildAutoRenewalBody({ ...PLACEHOLDER_PARTS, memberSuffix: getMemberSuffix(variables), url: donorDashboardUrl }, variables.locale), variables);
export const replaceRenewalEmailUrls = (body, urls) => {
    let result = body;
    if (urls.formUrl) {
        result = result.replace(new RegExp(FORM_URL_PLACEHOLDER.replace(/[{}]/g, "\\$&"), "g"), urls.formUrl);
    }
    if (urls.donorDashboardUrl) {
        result = result.replace(new RegExp(DONOR_DASHBOARD_URL_PLACEHOLDER.replace(/[{}]/g, "\\$&"), "g"), urls.donorDashboardUrl);
    }
    return result;
};
